import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { tint } from '@frontend/colors';
import { ScheduleFFHelpers } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { AvailabilityExceptionInfo, CalendarStripeLineInfo, WorkingHourInfo } from '../types';
import { zIndex } from '../utils';
import { useZoomSafeContext } from '../zoom';

const TIME_FORMAT = 'hh:mm A';
const MAX_DAY_MINUTES = 24 * 60;

enum IndicatorType {
  NonWorkingHours = 'non-working-hours', // this is the full day stripe background
  WorkingHours = 'working-hours', // this is the working hours gray background
  WorkingHoursNew = 'working-hours-new', // this is the working hours white background (To be replaced with WorkingHours once `schedule:calendar-block-ui` is fully rolled out)
  BreakHours = 'break-hours', // this is the break hours stripe background
  AvailabilityException = 'availability-exception', // this is the availability exception hours gray background
}

type IndicatorColorInfo = {
  background: string;
  stripe?: string;
};

const COLORS: Record<IndicatorType, IndicatorColorInfo> = {
  [IndicatorType.NonWorkingHours]: {
    background: theme.colors.neutral5,
    stripe: theme.colors.neutral20,
  },
  [IndicatorType.WorkingHours]: {
    background: theme.colors.neutral5,
  },
  [IndicatorType.WorkingHoursNew]: {
    background: theme.colors.white,
  },
  [IndicatorType.BreakHours]: {
    background: theme.colors.neutral5,
    stripe: theme.colors.neutral20,
  },
  [IndicatorType.AvailabilityException]: {
    background: theme.colors.neutral5,
  },
};

const DEBUG_COLORS: Record<IndicatorType, IndicatorColorInfo> = {
  [IndicatorType.NonWorkingHours]: {
    background: theme.colors.neutral5,
    stripe: theme.colors.neutral20,
  },
  [IndicatorType.WorkingHours]: {
    background: theme.colors.neutral5,
  },
  [IndicatorType.WorkingHoursNew]: {
    background: theme.colors.white,
  },
  [IndicatorType.BreakHours]: {
    background: theme.colors.critical5,
    stripe: theme.colors.critical20,
  },
  [IndicatorType.AvailabilityException]: {
    background: theme.colors.success10,
  },
};

const getStartMinutes = (startTime?: string): number =>
  startTime ? dayjs(startTime, TIME_FORMAT).diff(dayjs().startOf('day'), 'minute') : 0;

interface WorkingHoursIndicatorProps {
  workingHours?: WorkingHourInfo[];
  availabilityExceptions?: AvailabilityExceptionInfo[];
  isProvider?: boolean;
  hasAnyWorkingHoursSet?: boolean;
  isAllDayOff?: boolean;
  debug?: boolean;
}

export const WorkingHoursIndicator = ({
  workingHours,
  availabilityExceptions,
  isProvider = false,
  hasAnyWorkingHoursSet = true,
  isAllDayOff = false,
  debug = false,
}: WorkingHoursIndicatorProps) => {
  const { pixelsPerMinute } = useZoomSafeContext();
  // there should be only one working hour for selected date
  const workingHour = workingHours?.[0];

  const defaultProps = { debug, isProvider, pxPerMinutes: pixelsPerMinute };
  const calendarBlockUIFFInfo = ScheduleFFHelpers.useCalendarBlockUIFeatureFlag();

  return (
    <div css={{ width: '100%', height: '100%', position: 'absolute', zIndex: zIndex.workingHoursContainer, left: 0 }}>
      {/* when no working hours are set for location, the whole day need to be shown as non-working hours */}
      {((!isProvider && !hasAnyWorkingHoursSet) || isAllDayOff) && (
        <IndicatorItem
          startTime='00:00 AM'
          duration={MAX_DAY_MINUTES}
          type={IndicatorType.NonWorkingHours}
          {...defaultProps}
        />
      )}
      {hasAnyWorkingHoursSet && !isAllDayOff && (
        <>
          <IndicatorItem
            startTime='00:00 AM'
            duration={MAX_DAY_MINUTES}
            type={IndicatorType.NonWorkingHours}
            {...defaultProps}
          />
          <IndicatorItem
            startTime={workingHour?.startTime ?? ''}
            duration={workingHour?.duration ?? 0}
            type={calendarBlockUIFFInfo.isEnabled ? IndicatorType.WorkingHoursNew : IndicatorType.WorkingHours}
            {...defaultProps}
          />
          {workingHour?.breaks?.map(({ startTime, duration }) => (
            <IndicatorItem
              key={`break-hours-${startTime}-${duration}`}
              startTime={startTime}
              duration={duration}
              type={IndicatorType.BreakHours}
              {...defaultProps}
            />
          ))}
        </>
      )}
      {availabilityExceptions?.map(({ startTime, duration }) => (
        <IndicatorItem
          key={`availability-exception-${startTime}-${duration}`}
          startTime={startTime}
          duration={duration}
          type={IndicatorType.AvailabilityException}
          {...defaultProps}
        />
      ))}
    </div>
  );
};

interface IndicatorItemProps extends CalendarStripeLineInfo {
  type: IndicatorType;
  isProvider: boolean;
  debug: boolean;
  pxPerMinutes: number;
}

const IndicatorItem = ({ startTime, duration = 0, type, debug, isProvider, pxPerMinutes }: IndicatorItemProps) => {
  if (!startTime || duration <= 0) {
    return null;
  }

  const startMinutes = getStartMinutes(startTime);
  const { background, stripe } = debug ? DEBUG_COLORS[type] : COLORS[type];
  return (
    <div
      data-debug={debug ? `${type}-${startTime}-${duration}` : undefined}
      css={[
        hoursRectangleStyle(startMinutes, duration, pxPerMinutes),
        stripeBackground(background, stripe, isProvider),
      ]}
    />
  );
};

const hoursRectangleStyle = (startMinutes = 0, duration = 0, pxPerMinutes: number) => {
  // safeguard for negative start minutes
  if (startMinutes + duration > MAX_DAY_MINUTES) {
    duration = MAX_DAY_MINUTES - startMinutes;
  }

  return css({
    position: 'absolute',
    top: startMinutes * pxPerMinutes,
    height: duration * pxPerMinutes,
    width: '100%',
  });
};

const stripeBackground = (bgColor: string, stripeColor?: string, isProvider?: boolean) => {
  if (!stripeColor || stripeColor === bgColor) {
    return css({
      background: bgColor,
    });
  }

  return css({
    background: `repeating-linear-gradient(135deg, ${bgColor} 0 0.6rem, ${tint(
      isProvider ? 0.25 : 0.5,
      stripeColor
    )} 0.6rem 0.9rem)`,
    backgroundPosition: '0 0',
    backgroundRepeat: 'repeat',
    backgroundAttachment: 'fixed',
  });
};
